import {useMutation} from '@apollo/client'
import {Button, Modal, Spinner, Form} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {DUPLICATE_POLICY} from '../../../gql/mutations/policyMutation'
import {IResDuplicatePolicy} from '../soldTypes'
import {parseId} from '../../../helpers'
import {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {useNavigate} from 'react-router-dom'

interface IModalProps {
  id: string
  isOpen: boolean
  onClose: () => void
  refetch: () => void
}
export const ModalDuplicatePolicy: React.FC<IModalProps> = ({isOpen, onClose, id, refetch}) => {
  const intl = useIntl()
  const errMsg = (message: string) =>
    intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message})
  const [gglDuplicatePolicy, {data, loading, error}] = useMutation<IResDuplicatePolicy>(DUPLICATE_POLICY)

  // Possible status options: 'NO_CHANGE', 'RENEWED', 'CANCELED'
  const [statusOption, setStatusOption] = useState<'NO_CHANGE' | 'RENEWED' | 'CANCELED' | 'NO_CHANGE_AND_RENEW' >('NO_CHANGE')

  const navigate = useNavigate()

  useEffect(() => {
    if (error) toast.error(errMsg(error.message))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  useEffect(() => {
    if (!data) return

    if (data.duplicatePolicy.success) {
      toast.success(
        intl.formatMessage({
          id: 'policies.detail.modal.duplicate.toast.success',
          defaultMessage: 'Policy duplicated successfully',
        })
      )
      const newId = data.duplicatePolicy.policy?.id
      onClose()
      if (newId) {
        // redirigir a la nueva póliza duplicada
        navigate(`/one-quote/${newId}`)
      } else {
        refetch()
      }
    } else if (data.duplicatePolicy.errors && data.duplicatePolicy.errors.length > 0) {
      toast.error(errMsg(data.duplicatePolicy.errors[0].message))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleDuplicate = () => {
    gglDuplicatePolicy({
      variables: {
        id: parseId(id),
        statusOption: statusOption,
      },
    })
  }




  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          <FormattedMessage
            id='policies.detail.modal.duplicate.title'
            defaultMessage='Duplicate policy'
          />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h4 className='fs-2 mb-4'>
          <FormattedMessage
            id='policies.detail.modal.duplicate.body'
            defaultMessage='Are you sure you want to duplicate this policy?'
          />
        </h4>
        <div>
          <p className='mb-2'>
            <FormattedMessage
              id='policies.detail.modal.duplicate.question'
              defaultMessage='Do you want to change the status of the duplicated policy?'
            />
          </p>
          <Form>
            <div>
              <Form.Check
                type='radio'
                id='duplicate-no-change'
                name='statusOption'
                label={intl.formatMessage({id: 'policies.detail.modal.duplicate.opt.noChange', defaultMessage: 'No, do not change the status'})}
                value='NO_CHANGE'
                checked={statusOption === 'NO_CHANGE'}
                onChange={() => setStatusOption('NO_CHANGE')}
                className='mb-2'
              />
              <Form.Check
                type='radio'
                id='duplicate-renewed'
                name='statusOption'
                label={intl.formatMessage({id: 'policies.detail.modal.duplicate.opt.renewed', defaultMessage: 'Yes, mark this policy as Renewed'})}
                value='RENEWED'
                checked={statusOption === 'RENEWED'}
                onChange={() => setStatusOption('RENEWED')}
                className='mb-2'
              />
              <Form.Check
                type='radio'
                id='duplicate-canceled'
                name='statusOption'
                label={intl.formatMessage({id: 'policies.detail.modal.duplicate.opt.canceled', defaultMessage: 'Yes, mark this policy as Canceled'})}
                value='CANCELED'
                checked={statusOption === 'CANCELED'}
                onChange={() => setStatusOption('CANCELED')}
                className='mb-2'
              />
              <Form.Check
                type='radio'
                id='duplicate-no-change-renew'
                name='statusOption'
                label={intl.formatMessage({id: 'policies.detail.modal.duplicate.opt.noChangeRenew', defaultMessage: 'No, do not change the status of the original, but create as Renewed'})}
                value='NO_CHANGE_AND_RENEW'
                checked={statusOption === 'NO_CHANGE_AND_RENEW'}
                onChange={() => setStatusOption('NO_CHANGE_AND_RENEW')}
                className='mb-2'
              />
            </div>
          </Form>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' size='sm' onClick={onClose}>
          <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
        </Button>
        <Button variant='info' size='sm' onClick={handleDuplicate} disabled={loading}>
          {loading && <Spinner animation='border' size='sm' className='me-2' />} <FormattedMessage id='policies.list.page.act.duplicate' defaultMessage='Duplicate' />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
